import { PermissionEnum } from '@creditsights/api-ui-web';
import { Checkbox, Dropdown, Loader, ToasterMode } from '@creditsights/styleguide-web';
import { debounce, kebabCase } from 'lodash';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import useFetchUserEmailPreferences from '../../../hooks/user/useFetchUserEmailPreferences';
import useSaveUserEmailPreferences from '../../../hooks/user/useSaveUserEmailPreferences';
import { queryKeys } from '../../../hooks/queryKeyFactory';
import { emailSettings } from '../../../data/email-preferences';
import styles from './EmailPreferences.module.scss';
import { useToast } from '../../../hooks/toast/useToast';

const cx = classNames.bind(styles);

function EmailPreferences(): ReactElement {
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useFetchUserEmailPreferences();
  const { data: userPermissions, isLoading: isPermissionsLoading } = useFetchUserPermissions();
  const { mutate: saveEmailPreferences, isLoading: isSaving } = useSaveUserEmailPreferences();

  const researchAccess = useMemo(
    () => userPermissions?.permissions.includes(PermissionEnum.RESEARCH),
    [userPermissions]
  );

  const [tags, setSelectedTags] = useState<string[]>([]);
  const [pauseAllNotifications, setPauseAllNotifications] = useState(false);
  const [editorsPicks, setEditorsPicks] = useState(false);
  const [alertTimeZone, setAlertTimeZone] = useState<any>();
  const { showToast } = useToast();

  useEffect(() => {
    setSelectedTags(preferences?.tags ?? []);
    setEditorsPicks(preferences?.editorsPicks ?? false);
    setPauseAllNotifications(preferences?.pauseAllNotifications ?? false);
    setAlertTimeZone(preferences?.alertTimeZone);
  }, [preferences]);

  const isTagSelected = (tagId) => tags.indexOf(tagId) !== -1;

  const save = (newValues) => {
    saveEmailPreferences(newValues, {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.fetchEmailPrefrences]);
        showToast(<span>Email Preferences saved. </span>);
      },
      onError: () => {
        showToast('Saving Email Preferences failed.', ToasterMode.ERROR);
      },
    });
  };

  const debouncedSave = useMemo(() => debounce(save, 1000), []);

  const onTagToggle = (tagId, checked) => {
    const newValue = checked ? tags.filter((t) => t !== tagId) : [...tags, tagId];
    setSelectedTags(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks,
      pauseAllNotifications,
      tags: newValue,
    });
  };

  const onPauseAllNotificationsToggle = () => {
    const newValue = !pauseAllNotifications;
    setPauseAllNotifications(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks,
      pauseAllNotifications: newValue,
      tags,
    });
  };

  const onEditorPicksToggle = () => {
    const newValue = !editorsPicks;
    setEditorsPicks(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks: newValue,
      pauseAllNotifications,
      tags,
    });
  };

  const onTimeZoneSelected = (newValue) => {
    setAlertTimeZone(newValue);

    debouncedSave({
      alertTimeZone: newValue,
      editorsPicks,
      pauseAllNotifications,
      tags,
    });
  };

  const renderSources = ({ research = false, news = false, covenants = false }) => {
    return (
      <>
        {research && <div className={cx('source', 'research')}>Research</div>}
        {news && <div className={cx('source', 'news')}>News</div>}
        {covenants && <div className={cx('source', 'covenants')}>Covenants</div>}
      </>
    );
  };

  const renderTagPills = (items) => {
    return (
      <div className={cx('pills-ctx')}>
        {items.map((item) => {
          const isSelected = isTagSelected(item.tagId);

          return (
            <button
              type="button"
              className={cx('pill', `${isSelected ? 'active' : 'inactive'}`)}
              onClick={() => onTagToggle(item.tagId, isSelected)}
              disabled={isSaving}
              key={item.tagId}
            >
              <i className={cx('check', 'icon')} />
              <div className={cx('label')}>{item.name}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderToggle = (tagId) => {
    const checked = isTagSelected(tagId);
    const label = checked ? 'On' : 'Off';

    return (
      <Checkbox
        toggle
        dataTestId={tagId}
        checked={checked}
        label={label}
        onChange={() => onTagToggle(tagId, checked)}
        disabled={isSaving}
      />
    );
  };

  const renderTagToggles = (items) => {
    return items.map((item) => (
      <div className={cx('sub-section')} key={item.tagId}>
        <div className={cx('sub-title')}>{item.name}</div>
        <div className={cx('description')}>{item.description}</div>
        {renderToggle(item.tagId)}
      </div>
    ));
  };

  const renderTimezoneDropdown = (timezones) => {
    return (
      <div className={cx('dropdown-ctx')}>
        <div className={cx('label')}>Receive digest emails at 6:00am</div>
        <Dropdown
          value={alertTimeZone}
          disabled={isSaving}
          placeholder="Select timezone"
          options={timezones}
          onOptionSelect={(e) => onTimeZoneSelected(e)}
        />
      </div>
    );
  };

  const renderSection = (sectionConfig, mode: 'pills' | 'checkboxes') => {
    const { title, options, description, sources } = sectionConfig;

    return (
      <div className={cx('section')} id={kebabCase(title)}>
        <div className={cx('title')}>
          <div className={cx('text')}>{title}</div>
          {renderSources(sources)}
        </div>
        {description && <div className={cx('description')}>{description}</div>}
        {mode === 'checkboxes' && renderTagToggles(options)}
        {mode === 'pills' && renderTagPills(options)}
      </div>
    );
  };

  if (!isLoading && !isPermissionsLoading) {
    return (
      <div className={cx('cs-user-email-preferences')}>
        <div className={cx('preferences-ctx')}>
          <div className={cx('left-side-panel')}>
            <div className={cx('sub-header')}>
              <div className={cx('text')}>Preferences</div>
            </div>
            <div className={cx('all', 'section', 'pause-notifications-wrapper')}>
              <Checkbox
                toggle
                dataTestId="pause-all-notifications"
                checked={pauseAllNotifications}
                label="Pause all notifications"
                onChange={() => onPauseAllNotificationsToggle()}
                disabled={isSaving}
              />
            </div>
            {emailSettings.digests.timezones && renderTimezoneDropdown(emailSettings.digests.timezones)}
            <div className={cx('additional-section')}>
              <div className={cx('sub-header')}>
                <div className={cx('text')}>Additional Settings</div>
              </div>
              <div className={cx('links', 'section')}>
                <Link to="/my-content/interests">Manage email alerts for interests</Link>
                <Link to="/my-content/watchlists">Manage email alerts for watchlists</Link>
              </div>
            </div>
          </div>

          <div className={cx('contents')}>
            <div className={cx('main-header')}>
              <div className={cx('text')}>Subscriptions</div>
            </div>
            {renderSection(emailSettings.digests, 'checkboxes')}
            <div className={cx('section')}>
              <div className={cx('title')}>
                <div className={cx('text')}>Editor’s Picks</div>
                {renderSources({ research: true, news: true, covenants: true })}
              </div>
              <div className={cx('description')}>
                Editor’s Picks are featured articles selected by the CreditSights, Covenant Review, and LevFin Insights
                teams. Receive instant alerts on key research published within your interests instead of waiting for it
                in your daily summary.
              </div>
              <Checkbox
                toggle
                dataTestId="pause-all-notifications"
                checked={editorsPicks}
                label={editorsPicks ? 'On' : 'Off'}
                onChange={() => onEditorPicksToggle()}
                disabled={isSaving}
              />
            </div>
            {researchAccess && renderSection(emailSettings.morningComment, 'pills')}
            {researchAccess && renderSection(emailSettings.weeklyReader, 'pills')}
            {researchAccess && renderSection(emailSettings.summaries, 'checkboxes')}
          </div>
        </div>
      </div>
    );
  }
  return <Loader className={cx('cs-loader')} position="center" />;
}

export default EmailPreferences;
